import styled from 'styled-components'
import { AppModal } from '../AppModal/style'

export type ScoreTone = 'detractor' | 'passive' | 'promoter'

const toneColors: Record<ScoreTone, { background: string, border: string, color: string }> = {
  detractor: { background: '#fff5f5', border: '#f1cccc', color: '#8f3030' },
  passive: { background: '#fff9eb', border: '#ecdcae', color: '#745b16' },
  promoter: { background: '#eff9f4', border: '#c7e3d5', color: '#1d6848' }
}

export const SurveyModal = styled(AppModal)`
  && .ant-modal-body {
    max-height: calc(100dvh - 164px);
  }

  @media (max-width: 640px) {
    && .ant-modal-body {
      padding-inline: 8px;
    }
  }
`

export const Content = styled.div`
  display: grid;
  gap: 18px;
  color: #263957;
  font-family: var(--font-montserrat);
`

export const Intro = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 13px;
  border: 1px solid #dce7f6;
  border-radius: 12px;
  padding: 12px 14px;
  background: #f6f9fe;

  .intro-icon {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 9px;
    color: #fff;
    background: linear-gradient(135deg, #08469b, #0d62cc);
    box-shadow: 0 6px 14px rgba(18, 71, 144, 0.16);
  }

  strong {
    display: block;
    color: #17345f;
    font-size: 0.875rem;
  }

  p {
    margin: 4px 0 0;
    color: #65748c;
    font-size: 0.8125rem;
    line-height: 1.5;
  }
`

export const Section = styled.section`
  display: grid;
  gap: 11px;

  > header {
    display: grid;
    gap: 4px;
  }

  h3,
  p {
    margin: 0;
  }

  h3 {
    color: #162d55;
    font-size: 0.9rem;
    font-weight: 700;
    line-height: 1.45;
  }

  p {
    color: #708099;
    font-size: 0.78rem;
    line-height: 1.45;
  }
`

export const ScoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(11, minmax(34px, 1fr));
  gap: 5px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(11, minmax(0, 1fr));
    gap: 3px;
  }
`

export const ScoreButton = styled.button<{ $selected: boolean, $tone: ScoreTone }>`
  min-height: 38px;
  border: 1px solid ${({ $selected, $tone }) => $selected ? '#1262c0' : toneColors[$tone].border};
  border-radius: 8px;
  color: ${({ $selected, $tone }) => $selected ? '#fff' : toneColors[$tone].color};
  font: inherit;
  font-size: 0.875rem;
  font-weight: 700;
  background: ${({ $selected, $tone }) => $selected ? '#1262c0' : toneColors[$tone].background};
  box-shadow: ${({ $selected }) => $selected ? '0 5px 12px rgba(18, 98, 192, 0.2)' : 'none'};
  cursor: pointer;
  transition: border-color 160ms ease, background 160ms ease, color 160ms ease, transform 160ms ease;

  &:hover:not(:disabled) {
    border-color: #4c8ed8;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid #1262c0;
    outline-offset: 2px;
  }

  @media (max-width: 560px) {
    min-width: 0;
    min-height: 34px;
    border-radius: 6px;
    padding: 0;
    font-size: 0.75rem;
  }
`

export const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  color: #708099;
  font-size: 0.72rem;

  span:last-child {
    text-align: right;
  }
`

export const RatingsPanel = styled.div`
  display: grid;
  overflow: hidden;
  border: 1px solid #dce4ef;
  border-radius: 11px;
  background: #fff;
`

export const RatingRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  padding: 11px 12px;

  & + & {
    border-top: 1px solid #e8edf4;
  }

  > span {
    color: #32435e;
    font-size: 0.8rem;
    line-height: 1.45;
  }

  .ant-radio-group {
    white-space: nowrap;
  }

  .ant-radio-button-wrapper {
    min-width: 36px;
    padding-inline: 9px;
    text-align: center;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 8px;

    .ant-radio-group {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
    }

    .ant-radio-button-wrapper {
      padding-inline: 6px;
    }
  }
`

export const TextFields = styled.div`
  display: grid;
  gap: 14px;
`

export const TextField = styled.label`
  display: grid;
  gap: 7px;

  > span {
    color: #263957;
    font-size: 0.8125rem;
    font-weight: 650;
    line-height: 1.45;
  }
`

export const Thanks = styled.div`
  border-radius: 9px;
  padding: 9px 12px;
  color: #1d6848;
  font-size: 0.78rem;
  font-weight: 650;
  text-align: center;
  background: #eaf6f0;
`
